import React from 'react';
import classNames from 'classnames';
import { useTPAComponentsConfig } from '../TPAComponentsProvider';
import styles from './ActionsMenuLayout.module.scss';
/** Mirrors `ActionsMenuLayoutItemAlignment` from `wix-ui-tpa/cssVars` (types.js). */
export const ActionsMenuLayoutItemAlignment = {
    left: 'left',
    center: 'center',
    right: 'right',
};
/** `ACTIONS_MENU_ITEM_DATA_HOOK` in ActionsMenuLayout/dataHooks.js. */
const ACTIONS_MENU_ITEM_DATA_HOOK = 'actions-menu-item';
/**
 * `ActionsMenuLayout.Item` — an `<li>`, matching `ActionsMenuLayoutItem.js`:
 *
 *   li.itemRoot.item[role=menuitem][data-content][tabindex=-1]
 *     div.container
 *       div.iconContainer > div.icon        <- only with a prefixIcon
 *       div.textContainer
 *         div.text
 *           span.textRoot.content           <- a `Text`
 *           span.textRoot.subtitle          <- only with a subtitle
 *
 * `data-part` is kept on `item`, `text` and `content` because
 * actions-menu-items-desktop-styles.scss reaches all three from another CSS module (WUT's
 * `::item`, `::item::text`, `::item::content`).
 */
const Item = React.forwardRef(({ className, content, subtitle, prefixIcon, onClick, disabled = false, alignment, mobile: mobileProp, rtl: rtlProp, 'aria-label': ariaLabel, 'data-hook': dataHook = ACTIONS_MENU_ITEM_DATA_HOOK, }, ref) => {
    /*
     * `mobile` / `rtl` come from TPAComponentsContext when neither the caller nor the parent
     * layout passes them — `@wix/yoshi-flow-editor` wraps every widget in WUT's own
     * `TPAComponentsProvider`, which is how every WUT component here received them. The parent
     * still clones each Item with its own resolved `mobile`, so that keeps winning.
     */
    const tpaConfig = useTPAComponentsConfig();
    const mobile = mobileProp ?? tpaConfig.mobile ?? false;
    const rtl = rtlProp ?? tpaConfig.rtl ?? false;
    return (React.createElement("li", { ref: ref, role: "menuitem", tabIndex: -1, "aria-label": ariaLabel, "aria-disabled": disabled, "data-hook": dataHook, "data-content": typeof content === 'string' ? content : undefined, "data-part": "item", className: classNames(styles.itemRoot, styles.item, {
            [styles.itemMobile]: mobile,
            [styles.rtl]: rtl,
            [styles.disabled]: disabled,
            [styles.withIcon]: !!prefixIcon,
        }, alignment ? styles[`itemAlignment-${alignment}`] : undefined, className, 'has-custom-focus'), onClick: disabled ? undefined : onClick, onKeyDown: (e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onClick?.();
            }
        } },
        React.createElement("div", { className: styles.container },
            prefixIcon ? (React.createElement("div", { className: styles.iconContainer },
                React.createElement("div", { "data-part": "prefix-icon", className: styles.icon }, prefixIcon))) : null,
            React.createElement("div", { className: styles.textContainer },
                React.createElement("div", { "data-part": "text", className: styles.text },
                    React.createElement("span", { "data-part": "content", className: classNames(styles.textRoot, styles['textTypography-runningText'], styles['textPriority-primary'], 
                        /* `Text` reads TPAComponentsConsumer and emits its own `mobile` state, so the
                           item's label has to carry it too. Both columns read
                           `--wix-ui-tpa-text-main-text-font-size` first and differ only in the fallback —
                           14px/1.42em against 16px/1.5em — so this is invisible on a site that defines
                           the variable and 2px wrong on one that does not. */
                        { [styles.textMobile]: mobile }, styles.content) }, content),
                    subtitle ? (React.createElement("span", { className: classNames(styles.textRoot, styles['textTypography-runningText'], styles['textPriority-primary'], { [styles.textMobile]: mobile }, styles.subtitle) }, subtitle)) : null)))));
});
Item.displayName = 'ActionsMenuLayout.Item';
/**
 * Drop-in replacement for `ActionsMenuLayout` from `wix-ui-tpa/cssVars`, including the
 * `ActionsMenuLayout.Item` compound child the call sites use.
 *
 * The root is a `<ul role="menu" tabindex="0">`; it clones every Item child with the layout's
 * `item` class and an `alignment`, and owns arrow-key focus movement over their refs — all of
 * which is `ActionsMenuLayout.js`. Rendering a `div` of `button`s, as this port used to, left the
 * `ul`/`li` rules unmatched and the `mobile` state unreachable.
 */
const ActionsMenuLayoutBase = ({ className, children, focusedIndex, alignment, mobile: mobileProp, 'aria-label': ariaLabel, 'aria-labeledby': ariaLabeledBy, 'data-hook': dataHook, }) => {
    /* See the note on Item: the provider is mounted by yoshi, not by this app. */
    const tpaConfig = useTPAComponentsConfig();
    const mobile = mobileProp ?? tpaConfig.mobile ?? false;
    const itemRefs = React.useRef({});
    const [focusedIdx, setFocusedIdx] = React.useState(focusedIndex);
    const focusItem = React.useCallback((idx) => {
        setFocusedIdx(idx);
        itemRefs.current[idx]?.focus();
    }, []);
    React.useEffect(() => {
        if (typeof focusedIndex !== 'undefined') {
            focusItem(focusedIndex);
        }
    }, [focusedIndex, focusItem]);
    const count = () => Object.keys(itemRefs.current).length;
    const onKeyDown = (e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
            return;
        }
        /* Upstream prevents the page from scrolling on both. */
        e.preventDefault();
        const total = count();
        if (!total) {
            return;
        }
        const from = focusedIdx ?? 0;
        focusItem(e.key === 'ArrowDown' ? (from + 1) % total : (from - 1 + total) % total);
    };
    let index = -1;
    return (React.createElement("ul", { role: "menu", tabIndex: 0, "data-mobile": mobile, "data-hook": dataHook, "aria-label": ariaLabel, "aria-labelledby": ariaLabeledBy, className: classNames(styles.root, { [styles.mobile]: mobile }, className), onKeyDown: onKeyDown, onFocus: (e) => {
            /* Focus the first item when the list itself is tab-focused, not when a child is. */
            const onChild = Object.values(itemRefs.current).some((el) => el === e.target);
            if (!onChild) {
                focusItem(0);
            }
        }, onMouseDown: (e) => e.preventDefault() }, React.Children.map(children, (child) => {
        if (!React.isValidElement(child) || child.type !== Item) {
            return child;
        }
        index += 1;
        const itemIndex = index;
        return React.cloneElement(child, {
            className: styles.item,
            alignment,
            mobile,
            ref: (el) => {
                itemRefs.current[itemIndex] = el;
            },
        });
    })));
};
export const ActionsMenuLayout = ActionsMenuLayoutBase;
ActionsMenuLayout.Item = Item;
//# sourceMappingURL=ActionsMenuLayout.js.map